import React from "react";

/**
 * Photographic mentor avatar library for Ascend Higher.
 *
 * Each mentor renders a real photographic portrait (generated via Gemini Nano
 * Banana, saved at /mentors/{id}.png in the public folder). The wrapper adds
 * subtle breathing, pulse rings on listening/speaking, and a warm halo so the
 * avatars feel alive without being uncanny.
 *
 * `state` prop: idle | listening | thinking | speaking
 */

const baseStyle = (state) => ({
  position: "relative",
  display: "inline-block",
  transformOrigin: "center bottom",
  animation:
    state === "speaking"
      ? "ascendBreatheFast 1.1s ease-in-out infinite"
      : "ascendBreathe 4.5s ease-in-out infinite",
});

const Wrapper = ({ size = 220, state = "idle", children }) => (
  <div style={{ width: size, height: size, ...baseStyle(state) }} className="ascend-avatar">
    {state === "speaking" && (
      <span
        aria-hidden
        className="absolute inset-0 rounded-full"
        style={{
          boxShadow:
            "0 0 0 8px rgba(72,106,143,0.18), 0 0 0 24px rgba(72,106,143,0.08)",
          animation: "ascendPulseRing 1.5s ease-out infinite",
        }}
      />
    )}
    {state === "listening" && (
      <span
        aria-hidden
        className="absolute inset-0 rounded-full"
        style={{
          boxShadow:
            "0 0 0 4px rgba(62,107,82,0.18), 0 0 0 14px rgba(62,107,82,0.06)",
          animation: "ascendPulseRing 2.2s ease-out infinite",
        }}
      />
    )}
    {children}
  </div>
);

// ---------- MENTORS ----------
// Each mentor maps to a /mentors/{photoFile}.png in the public folder.
export const MENTORS = [
  {
    id: "human_maya",
    category: "human",
    label: "Maya",
    subtitle: "Calm & grounding",
    bio: "Speaks slowly. Helps you feel safer when life gets loud.",
    voice: { gender: "female", accent: "american" },
    photoFile: "maya.png",
  },
  {
    id: "human_james",
    category: "human",
    label: "James",
    subtitle: "Direct & steady",
    bio: "No fluff. Names the next move and helps you commit.",
    voice: { gender: "male", accent: "american" },
    photoFile: "james.png",
  },
  {
    id: "human_elder",
    category: "human",
    label: "Elder Wisdom",
    subtitle: "Wise & patient",
    bio: "Listens deeply. Speaks rarely. Says exactly what you need.",
    voice: { gender: "male", accent: "british" },
    photoFile: "elder.png",
  },
  {
    id: "human_sofia",
    category: "human",
    label: "Sofia",
    subtitle: "Warm & encouraging",
    bio: "Bright, present, generous. Makes hard chapters feel survivable.",
    voice: { gender: "female", accent: "american" },
    photoFile: "sofia.png",
  },
];

// ---------- PHOTO COMPONENT ----------
function PhotoMentor({ photoFile, label, state }) {
  return (
    <div className="relative w-full h-full rounded-full overflow-hidden bg-[#eadfc9] shadow-[0_18px_50px_-18px_rgba(15,23,36,0.35)]">
      <img
        src={`/mentors/${photoFile}`}
        alt={label}
        className="w-full h-full object-cover"
        draggable={false}
        onError={(e) => {
          // graceful fallback if asset missing in deployed env
          e.currentTarget.style.display = "none";
        }}
      />
      {/* Subtle warm overlay to unify lighting across mentors */}
      <div
        aria-hidden
        className="absolute inset-0 rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at 30% 20%, rgba(255,255,255,0.10), transparent 55%)",
        }}
      />
      {/* Soft "speaking" mouth-area glow */}
      {state === "speaking" && (
        <div
          aria-hidden
          className="absolute inset-0 rounded-full pointer-events-none"
          style={{
            background:
              "radial-gradient(circle at 50% 78%, rgba(72,106,143,0.25), transparent 35%)",
            animation: "ascendBreatheFast 1.1s ease-in-out infinite",
          }}
        />
      )}
    </div>
  );
}

export function getMentor(id) {
  return MENTORS.find((m) => m.id === id) || MENTORS[0];
}

export default function Avatar({ id = "human_maya", size = 220, state = "idle" }) {
  const mentor = getMentor(id);
  return (
    <Wrapper size={size} state={state}>
      <PhotoMentor photoFile={mentor.photoFile} label={mentor.label} state={state} />
    </Wrapper>
  );
}
